import React from "react";
import { ChatThreadMessage, ExpertPersona, ModalKind } from "./types";
import { ProofCard } from "./cards/ProofCard";
import { ProfileCard } from "./cards/ProfileCard";
import { DMCard } from "./cards/DMCard";
import { ConversationControlRenderer } from "../../../packages/conversation-react/src";

type ColorTheme = { bg: string; text: string; border: string; accent: string };

const categoryThemes: Record<string, ColorTheme> = {
  leadership: {
    bg: "bg-slate-50",
    text: "text-slate-700",
    border: "border-slate-100",
    accent: "bg-slate-600",
  },
  engineering: {
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    border: "border-indigo-100",
    accent: "bg-indigo-600",
  },
  intelligence: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-100",
    accent: "bg-amber-600",
  },
  operations: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-100",
    accent: "bg-emerald-600",
  },
  career: {
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-100",
    accent: "bg-rose-600",
  },
  legal: { bg: "bg-red-50", text: "text-red-700", border: "border-red-100", accent: "bg-red-600" },
  system: {
    bg: "bg-gray-50",
    text: "text-gray-500",
    border: "border-gray-100",
    accent: "bg-gray-500",
  },
};

function getExpertTheme(expert: ExpertPersona): ColorTheme {
  const e = expert.toLowerCase();
  if (e.includes("ceo") || e.includes("strategy") || e.includes("vision") || e.includes("offer"))
    return categoryThemes.leadership;
  if (
    e.includes("architect") ||
    e.includes("engineer") ||
    e.includes("devops") ||
    e.includes("foundation") ||
    e.includes("polish")
  )
    return categoryThemes.engineering;
  if (
    e.includes("analyst") ||
    e.includes("mapper") ||
    e.includes("intel") ||
    e.includes("mitigator") ||
    e.includes("spotter")
  )
    return categoryThemes.intelligence;
  if (
    e.includes("scout") ||
    e.includes("coordinator") ||
    e.includes("ops") ||
    e.includes("optimizer") ||
    e.includes("governance") ||
    e.includes("quality") ||
    e.includes("steward")
  )
    return categoryThemes.operations;
  if (
    e.includes("coach") ||
    e.includes("strategist") ||
    e.includes("specialist") ||
    e.includes("curator") ||
    e.includes("narrative")
  )
    return categoryThemes.career;
  if (
    e.includes("legal") ||
    e.includes("ethics") ||
    e.includes("lead") ||
    e.includes("inclusion") ||
    e.includes("compliance") ||
    e.includes("consultant")
  )
    return categoryThemes.legal;
  return categoryThemes.system;
}

const modalActionLabel: Record<NonNullable<ModalKind>, string> = {
  "add-proof": "Add proof",
  "edit-profile": "Edit profile",
  "compose-dm": "Compose message",
};

type Props = {
  message: ChatThreadMessage;
  onOpenModal?: (modal: ModalKind) => void;
  onControlChange?: (messageId: string, value: unknown) => void;
  onControlSubmit?: (messageId: string) => void;
};

export const ChatMessage: React.FC<Props> = ({
  message,
  onOpenModal,
  onControlChange,
  onControlSubmit,
}) => {
  switch (message.type) {
    case "stage_marker":
      return (
        <div className="flex items-center gap-3 py-2">
          <div className="flex-1 h-px bg-black/8" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted px-3 py-1 rounded-full bg-brand-surface border border-black/6">
            {message.label}
          </span>
          <div className="flex-1 h-px bg-black/8" />
        </div>
      );

    case "ai": {
      const colors = getExpertTheme(message.expert);
      const showSubmit = message.controlStep && message.controlStep.control !== "choice";

      return (
        <div className="flex gap-3 items-start animate-in fade-in slide-in-from-left-4 duration-300">
          <div
            className={`flex-none w-8 h-8 rounded-xl ${colors.bg} border ${colors.border} flex items-center justify-center mt-0.5 shadow-sm`}
          >
            <span className={`text-[9px] font-black uppercase tracking-tight ${colors.text}`}>
              {message.expert
                .split(/[/\s]+/)
                .map((w) => w[0])
                .join("")
                .slice(0, 2)}
            </span>
          </div>
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center gap-2">
              <p className={`text-[10px] font-bold uppercase tracking-[0.16em] ${colors.text}`}>
                {message.expert}
              </p>
              {message.expertGated && (
                <span className="text-[8px] font-black uppercase tracking-widest bg-black text-white px-1.5 py-0.5 rounded">
                  Expert Review
                </span>
              )}
            </div>

            <div
              className={`${colors.bg} border ${colors.border} rounded-2xl rounded-tl-sm px-4 py-3 max-w-[90%] shadow-sm relative group`}
            >
              <p className="text-sm text-brand-ink leading-relaxed whitespace-pre-wrap">
                {message.text}
              </p>

              {message.expertGated && message.expertReviewStatus === "pending" && (
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center rounded-2xl">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-accent animate-pulse" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                      Awaiting Strategist...
                    </span>
                  </div>
                </div>
              )}
            </div>

            {message.controlStep && (
              <div className="pl-1 pr-4 py-2 space-y-4 max-w-[90%]">
                <ConversationControlRenderer
                  step={message.controlStep}
                  value={message.controlValue}
                  invalid={Boolean(message.controlError)}
                  onChange={(val) => onControlChange?.(message.id, val)}
                  autoFocus
                />

                {message.controlError && (
                  <p className="text-xs text-red-600 font-medium ml-2 animate-in fade-in duration-200">
                    {message.controlError}
                  </p>
                )}

                {showSubmit && (
                  <button
                    onClick={() => onControlSubmit?.(message.id)}
                    className={`${colors.accent} text-white px-6 py-2.5 rounded-full text-xs font-bold shadow-md hover:brightness-110 active:scale-95 transition-all w-fit`}
                  >
                    Save and continue
                  </button>
                )}
              </div>
            )}

            {message.modalSuggestion && onOpenModal && (
              <button
                onClick={() => onOpenModal(message.modalSuggestion!)}
                className={`text-xs font-bold ${colors.text} hover:underline flex items-center gap-1 ml-1`}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M6 1v10M1 6h10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                {modalActionLabel[message.modalSuggestion]}
              </button>
            )}
          </div>
        </div>
      );
    }

    case "user":
      return (
        <div className="flex justify-end animate-in fade-in slide-in-from-right-4 duration-300">
          <div className="bg-brand-surface border border-black/5 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[85%] shadow-sm">
            <p className="text-sm text-brand-ink leading-relaxed">{message.text}</p>
          </div>
        </div>
      );

    case "proof_card":
      return <ProofCard proofId={message.proofId} />;

    case "profile_card":
      return <ProfileCard profileId={message.profileId} />;

    case "dm_card":
      return <DMCard dmId={message.dmId} />;

    case "system":
      return (
        <div className="flex justify-center items-center">
          <span className="text-xs text-brand-muted bg-brand-surface border border-black/5 px-3 py-1 rounded-full">
            {message.text}
          </span>
        </div>
      );

    default:
      return null;
  }
};
